import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import {
  downloadLaboralReport,
  laboralExportErrorMessage,
  MSG_SIN_DATOS_EXPORTAR,
} from "@/lib/laboralReportExport";
import { PageHeader } from "@/pages/layoutChrome";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileUp,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const CONTEXT_LABEL = "Asistente Laboral y REPSE";
const ALL_PATRONALES = "__all__";

function diffFieldSet(row) {
  return new Set((row?.diferencias || []).map((d) => d.campo));
}

function pickNombre(row) {
  return row?.nombre_sistema || row?.nombre_emision
    || row?.sistema?.nombre_completo
    || row?.emision?.nombre_completo
    || "—";
}

function pickField(row, campo, side) {
  const src = side === "emision" ? row?.emision : row?.sistema;
  if (src?.[campo]) return src[campo];
  const dif = (row?.diferencias || []).find((d) => d.campo === campo);
  if (dif) return dif[side] ?? "";
  return src?.[campo] ?? "";
}

function mismatchClass(campo, diffs) {
  return diffs.has(campo) ? "bg-amber-100 text-amber-950 font-medium" : "";
}

function EmptyRow({ colSpan, message }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-center text-xs text-slate-500 py-10">
        {message}
      </TableCell>
    </TableRow>
  );
}

function CoincidenciasTable({ rows }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-[10px] min-w-[160px]">Nombre</TableHead>
          <TableHead className="text-[10px]">NSS</TableHead>
          <TableHead className="text-[10px]">RFC</TableHead>
          <TableHead className="text-[10px]">SDI</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.map((row) => {
          const w = row.sistema || row.emision || {};
          return (
            <TableRow key={row.clave}>
              <TableCell className="text-xs">{pickNombre(row)}</TableCell>
              <TableCell className="text-xs font-mono">{w.nss || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.rfc || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.sdi || "—"}</TableCell>
            </TableRow>
          );
        }) : (
          <EmptyRow colSpan={4} message="Sin coincidencias en esta comparación." />
        )}
      </TableBody>
    </Table>
  );
}

function DiferenciasCategoriaTable({ rows, emptyMessage }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-[10px] min-w-[140px]">Nombre (sistema)</TableHead>
          <TableHead className="text-[10px] min-w-[140px]">Nombre (emisión)</TableHead>
          <TableHead className="text-[10px]">Campo</TableHead>
          <TableHead className="text-[10px]">Valor sistema</TableHead>
          <TableHead className="text-[10px]">Valor emisión</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.flatMap((row) => (
          (row.diferencias || []).map((d, idx) => (
            <TableRow key={`${row.clave}-${d.campo}-${idx}`}>
              <TableCell className="text-xs">{row.nombre_sistema || row.sistema?.nombre_completo || "—"}</TableCell>
              <TableCell className="text-xs">{row.nombre_emision || row.emision?.nombre_completo || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{d.campo || "—"}</TableCell>
              <TableCell className="text-xs font-mono bg-amber-50">{d.sistema || "—"}</TableCell>
              <TableCell className="text-xs font-mono bg-amber-50">{d.emision || "—"}</TableCell>
            </TableRow>
          ))
        )) : (
          <EmptyRow colSpan={5} message={emptyMessage} />
        )}
      </TableBody>
    </Table>
  );
}

function DiferenciasTable({ rows }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-[10px] min-w-[140px]">Nombre (sistema)</TableHead>
          <TableHead className="text-[10px] min-w-[140px]">Nombre (emisión)</TableHead>
          <TableHead className="text-[10px]">NSS sistema</TableHead>
          <TableHead className="text-[10px]">NSS emisión</TableHead>
          <TableHead className="text-[10px]">RFC sistema</TableHead>
          <TableHead className="text-[10px]">RFC emisión</TableHead>
          <TableHead className="text-[10px]">SDI sistema</TableHead>
          <TableHead className="text-[10px]">SDI emisión</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.map((row) => {
          const diffs = diffFieldSet(row);
          return (
            <TableRow key={row.clave}>
              <TableCell className="text-xs">{row.nombre_sistema || row.sistema?.nombre_completo || "—"}</TableCell>
              <TableCell className="text-xs">{row.nombre_emision || row.emision?.nombre_completo || "—"}</TableCell>
              <TableCell className={`text-xs font-mono ${mismatchClass("nss", diffs)}`}>
                {pickField(row, "nss", "sistema") || "—"}
              </TableCell>
              <TableCell className={`text-xs font-mono ${mismatchClass("nss", diffs)}`}>
                {pickField(row, "nss", "emision") || "—"}
              </TableCell>
              <TableCell className={`text-xs font-mono ${mismatchClass("rfc", diffs)}`}>
                {pickField(row, "rfc", "sistema") || "—"}
              </TableCell>
              <TableCell className={`text-xs font-mono ${mismatchClass("rfc", diffs)}`}>
                {pickField(row, "rfc", "emision") || "—"}
              </TableCell>
              <TableCell className={`text-xs font-mono ${mismatchClass("sdi", diffs)}`}>
                {pickField(row, "sdi", "sistema") || "—"}
              </TableCell>
              <TableCell className={`text-xs font-mono ${mismatchClass("sdi", diffs)}`}>
                {pickField(row, "sdi", "emision") || "—"}
              </TableCell>
            </TableRow>
          );
        }) : (
          <EmptyRow colSpan={8} message="No hay diferencias entre emisión y catálogo." />
        )}
      </TableBody>
    </Table>
  );
}

function MovimientosTable({ rows }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-[10px]">NSS</TableHead>
          <TableHead className="text-[10px]">Tipo</TableHead>
          <TableHead className="text-[10px]">Fecha</TableHead>
          <TableHead className="text-[10px] min-w-[200px]">Mensaje</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.map((row, idx) => (
          <TableRow key={`${row.nss}-${row.codigo}-${idx}`}>
            <TableCell className="text-xs font-mono">{row.nss || "—"}</TableCell>
            <TableCell className="text-xs">{row.tipo_movimiento || "—"}</TableCell>
            <TableCell className="text-xs font-mono">{row.fecha_movimiento || row.fecha_sistema || "—"}</TableCell>
            <TableCell className="text-xs">{row.mensaje || "—"}</TableCell>
          </TableRow>
        )) : (
          <EmptyRow colSpan={4} message="Sin alertas de movimientos (altas/bajas/fechas)." />
        )}
      </TableBody>
    </Table>
  );
}

function SoloSistemaTable({ rows }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-[10px] min-w-[160px]">Nombre</TableHead>
          <TableHead className="text-[10px]">NSS</TableHead>
          <TableHead className="text-[10px]">RFC</TableHead>
          <TableHead className="text-[10px]">SDI</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.map((row) => {
          const w = row.sistema || {};
          return (
            <TableRow key={row.clave}>
              <TableCell className="text-xs">{w.nombre_completo || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.nss || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.rfc || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.sdi || "—"}</TableCell>
            </TableRow>
          );
        }) : (
          <EmptyRow colSpan={4} message="Todos los trabajadores del catálogo aparecen en la emisión IMSS." />
        )}
      </TableBody>
    </Table>
  );
}

function SoloEmisionTable({ rows }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-[10px] min-w-[160px]">Nombre</TableHead>
          <TableHead className="text-[10px]">NSS</TableHead>
          <TableHead className="text-[10px]">RFC</TableHead>
          <TableHead className="text-[10px]">SDI</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.map((row) => {
          const w = row.emision || {};
          return (
            <TableRow key={row.clave}>
              <TableCell className="text-xs">{w.nombre_completo || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.nss || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.rfc || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{w.sdi || "—"}</TableCell>
            </TableRow>
          );
        }) : (
          <EmptyRow colSpan={4} message="Todos los registros de la emisión IMSS están en el catálogo." />
        )}
      </TableBody>
    </Table>
  );
}

function FilePickButton({ label, hint, accept, disabled, busy, fileName, onPick, testId }) {
  const inputRef = useRef(null);
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 text-xs"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          data-testid={testId}
        >
          <FileUp className="w-3.5 h-3.5 mr-1.5" />
          Seleccionar archivo
        </Button>
        <span className="text-xs text-muted-foreground truncate max-w-[220px]">
          {fileName || "Sin archivo"}
        </span>
      </div>
      {hint ? <p className="text-[10px] text-muted-foreground m-0">{hint}</p> : null}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files?.[0] || null);
          e.target.value = "";
        }}
      />
    </div>
  );
}

/**
 * Confronta EMA/EBA del IMSS vs catálogo interno de trabajadores.
 */
export default function ConfrontaEmisionesImss() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [patronales, setPatronales] = useState([]);
  const [registroPatronalId, setRegistroPatronalId] = useState(ALL_PATRONALES);
  const [fileEma, setFileEma] = useState(null);
  const [fileEba, setFileEba] = useState(null);
  const [fileMov, setFileMov] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("coincidencias");

  const instrucciones = config?.instrucciones || "";
  const counts = useMemo(() => ({
    coincidencias: result?.coincidencias?.length || 0,
    diferencias: result?.diferencias?.length || 0,
    sdi: result?.diferencias_sdi?.length || 0,
    infonavit: result?.diferencias_infonavit?.length || 0,
    fechas: result?.diferencias_fechas?.length || 0,
    identidad: result?.diferencias_identidad?.length || 0,
    movimientos: result?.movimientos_alertas?.length || 0,
    soloSistema: result?.solo_sistema?.length || 0,
    soloEmision: result?.solo_emision?.length || 0,
  }), [result]);

  const loadPatronales = useCallback(async () => {
    if (!clientId) {
      setPatronales([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/registros-patronales", { params: { client_id: clientId } });
      setPatronales(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setPatronales([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar registros patronales");
    }
  }, [clientId]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      setPatronales([]);
      setResult(null);
      return;
    }
    api
      .get("/laboral/confronta-emisiones/config", { params: { client_id: clientId } })
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(null));
    loadPatronales();
    setRegistroPatronalId(ALL_PATRONALES);
    setResult(null);
    setFileEma(null);
    setFileEba(null);
    setFileMov(null);
  }, [clientId, loadPatronales]);

  const onComparar = async () => {
    if (!clientId || !fileEma) {
      toast.error("Seleccione cliente y cargue la emisión mensual (EMA).");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      if (registroPatronalId && registroPatronalId !== ALL_PATRONALES) {
        body.append("registro_patronal_id", registroPatronalId);
      }
      body.append("archivo_ema", fileEma);
      if (fileEba) body.append("archivo_eba", fileEba);
      if (fileMov) body.append("archivo_movimientos", fileMov);
      const { data } = await api.post("/laboral/confronta-emisiones/comparar", body, { timeout: 120000 });
      setResult(data);
      if (data.tiene_diferencias) {
        const next = data.diferencias?.length ? "diferencias"
          : data.diferencias_sdi?.length ? "sdi"
          : data.diferencias_infonavit?.length ? "infonavit"
          : data.movimientos_alertas?.length ? "movimientos"
          : data.solo_sistema?.length ? "solo_sistema"
          : "solo_emision";
        setActiveTab(next);
        toast.warning("La comparación encontró diferencias. Revise las pestañas resaltadas.");
      } else {
        setActiveTab("coincidencias");
        toast.success(`Comparación completada: ${data.count_emision || 0} emisión · ${data.count_sistema || 0} catálogo · todo coincide.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo comparar las emisiones IMSS");
    } finally {
      setBusy(false);
    }
  };

  const onExportarExcel = async () => {
    if (!clientId || !result?.ok) {
      toast.warning(MSG_SIN_DATOS_EXPORTAR);
      return;
    }
    setExporting(true);
    try {
      const filename = await downloadLaboralReport({
        endpoint: "/laboral/confronta-emisiones/exportar-excel",
        payload: {
          client_id: clientId,
          periodo_nota: [
            result.fuentes?.ema ? "EMA" : null,
            result.fuentes?.eba ? "EBA" : null,
            result.fuentes?.movimientos ? "Movimientos" : null,
          ].filter(Boolean).join(" + ") || "Confronta emisiones",
          resultado: result,
        },
        fallbackFilename: "confronta_emisiones_imss.xlsx",
      });
      toast.success(`Reporte descargado: ${filename}`);
    } catch (e) {
      toast.error(await laboralExportErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="confronta-emisiones-imss-page">
      <PageHeader
        title="Confronta emisiones IMSS"
        subtitle={CONTEXT_LABEL}
        description="Compare EMA (mensual) y EBA (bimestral) del IDSE contra el catálogo de trabajadores."
        breadcrumb={[CONTEXT_LABEL, "Confronta emisiones IMSS"]}
      />

      <LaboralFlujoOperativo>
      <div className="space-y-6">
      {!clientId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Seleccione un cliente de trabajo en el Panel General.
        </div>
      )}

      {instrucciones ? (
        <div className="rounded-md border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 leading-relaxed flex gap-2">
          <HelpCircle className="w-4 h-4 shrink-0 text-sky-700 mt-0.5" />
          <p className="m-0">{instrucciones}</p>
        </div>
      ) : null}

      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
        <h3 className="text-sm font-medium text-[#0F2D5C]">Parámetros de confronta</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente (patrón)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-10" data-testid="confronta-emisiones-client-select">
                <SelectValue placeholder="Seleccione cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social} — {c.rfc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Registro patronal (opcional)</Label>
            <Select
              value={registroPatronalId}
              onValueChange={setRegistroPatronalId}
              disabled={!clientId}
            >
              <SelectTrigger className="h-10 text-xs" data-testid="confronta-emisiones-patron-select">
                <SelectValue placeholder="Todos los trabajadores del cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PATRONALES}>Todos los trabajadores del cliente</SelectItem>
                {patronales.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.registro_patronal} — {p.nombre_sucursal_obra || p.razon_social_imss || "Patrón"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
          <FilePickButton
            label="Emisión mensual (EMA) *"
            hint="Excel (.xlsx/.xls) o TXT pipe del IDSE"
            accept=".xlsx,.xls,.txt"
            disabled={!clientId}
            busy={busy}
            fileName={fileEma?.name}
            onPick={setFileEma}
            testId="input-ema-emision"
          />
          <FilePickButton
            label="Emisión bimestral (EBA)"
            hint="Excel — aportación y amortización INFONAVIT"
            accept=".xlsx,.xls"
            disabled={!clientId}
            busy={busy}
            fileName={fileEba?.name}
            onPick={setFileEba}
            testId="input-eba-emision"
          />
          <FilePickButton
            label="Movimientos (opcional)"
            hint="Altas/bajas — Excel o TXT"
            accept=".xlsx,.xls,.txt"
            disabled={!clientId}
            busy={busy}
            fileName={fileMov?.name}
            onPick={setFileMov}
            testId="input-movimientos-emision"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Button
            type="button"
            className="bg-[#0F2D5C] hover:bg-[#0a2247]"
            disabled={busy || !clientId || !fileEma}
            onClick={onComparar}
            data-testid="btn-comparar-emision-imss"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
            Comparar
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={exporting || !result?.ok}
            onClick={onExportarExcel}
            data-testid="btn-exportar-excel-emision"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar Excel
          </Button>
        </div>
      </section>

      {result ? (
        <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-[#0F2D5C] m-0">Resultados de la comparación</h3>
              <p className="text-xs text-muted-foreground mt-1 m-0">
                Emisión IMSS: {result.count_emision ?? 0} · Catálogo sistema: {result.count_sistema ?? 0}
                {result.fuentes?.eba ? "" : " · Sin EBA (INFONAVIT no validado)"}
              </p>
            </div>
            {result.tiene_diferencias ? (
              <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Se encontraron diferencias o registros huérfanos
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Coincidencia total entre emisión y catálogo
              </div>
            )}
          </div>

          {(result.avisos || []).length ? (
            <div className="rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-950 space-y-1">
              {result.avisos.map((av) => (
                <p key={av} className="m-0 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  {av}
                </p>
              ))}
            </div>
          ) : null}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="coincidencias" data-testid="tab-coincidencias">
                Coincidencias ({counts.coincidencias})
              </TabsTrigger>
              <TabsTrigger value="diferencias" data-testid="tab-diferencias">
                Diferencias ({counts.diferencias})
              </TabsTrigger>
              <TabsTrigger value="sdi" data-testid="tab-sdi">
                SDI ({counts.sdi})
              </TabsTrigger>
              <TabsTrigger value="infonavit" data-testid="tab-infonavit">
                INFONAVIT ({counts.infonavit})
              </TabsTrigger>
              <TabsTrigger value="fechas" data-testid="tab-fechas">
                Fechas ({counts.fechas})
              </TabsTrigger>
              <TabsTrigger value="movimientos" data-testid="tab-movimientos">
                Movimientos ({counts.movimientos})
              </TabsTrigger>
              <TabsTrigger value="solo_sistema" data-testid="tab-solo-sistema">
                Solo en sistema ({counts.soloSistema})
              </TabsTrigger>
              <TabsTrigger value="solo_emision" data-testid="tab-solo-emision">
                Solo en emisión ({counts.soloEmision})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="coincidencias" className="mt-4 overflow-x-auto rounded-md border">
              <CoincidenciasTable rows={result.coincidencias || []} />
            </TabsContent>
            <TabsContent value="diferencias" className="mt-4 overflow-x-auto rounded-md border">
              <DiferenciasTable rows={result.diferencias || []} />
            </TabsContent>
            <TabsContent value="sdi" className="mt-4 overflow-x-auto rounded-md border">
              <DiferenciasCategoriaTable
                rows={result.diferencias_sdi || []}
                emptyMessage="No hay diferencias de SDI / SBC."
              />
            </TabsContent>
            <TabsContent value="infonavit" className="mt-4 overflow-x-auto rounded-md border">
              <DiferenciasCategoriaTable
                rows={result.diferencias_infonavit || []}
                emptyMessage="No hay diferencias de aportación o amortización INFONAVIT (requiere EBA)."
              />
            </TabsContent>
            <TabsContent value="fechas" className="mt-4 overflow-x-auto rounded-md border">
              <DiferenciasCategoriaTable
                rows={result.diferencias_fechas || []}
                emptyMessage="No hay diferencias de fechas de alta."
              />
            </TabsContent>
            <TabsContent value="movimientos" className="mt-4 overflow-x-auto rounded-md border">
              <MovimientosTable rows={result.movimientos_alertas || []} />
            </TabsContent>
            <TabsContent value="solo_sistema" className="mt-4 overflow-x-auto rounded-md border">
              <SoloSistemaTable rows={result.solo_sistema || []} />
            </TabsContent>
            <TabsContent value="solo_emision" className="mt-4 overflow-x-auto rounded-md border">
              <SoloEmisionTable rows={result.solo_emision || []} />
            </TabsContent>
          </Tabs>
        </section>
      ) : (
        <section className="rounded-lg border border-dashed bg-slate-50/50 p-8 text-center text-sm text-muted-foreground">
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Comparando emisiones…
            </span>
          ) : (
            "Cargue EMA (obligatorio), EBA y movimientos opcionales, luego pulse Comparar."
          )}
        </section>
      )}
      </div>
      </LaboralFlujoOperativo>
    </div>
  );
}
